import { Dispatch } from "redux";

import {
  PHONEME_IMPORT_SPEC,
  PHONEME_BACK_TO_INTRO,
  PHONEME_BALLOON_START,
  PHONEME_BASKET_START,
  PHONEME_CLOUD_START,
  PHONEME_CLOUD_SET_ROUND,
  PHONEME_BALLOON_SELECT,
  PHONEME_BALLON_END,
  PHONEME_BASKET_SELECT,
  PHONEME_BALLOON_READY,
  PHONEME_BALLON_NOT_CLICKABLE,
  PHONEME_BALLOON_CLEAR_ANSWER,
  PHONEME_BALLON_READY_END,
  PHONEME_CLOUD_END,
  PHONEME_CLOUD_PLAY,
  PHONEME_CLOUD_PLAY_END,
  PHONEME_CLOUD_PLAY_NEXT,
  PHONEME_CLOUD_CLICK,
  PHONEME_BASKET_RESPONSE,
  PHONEME_BASKET_CLEAR,
  PHONEME_BASKET_END,
  PHONEME_BASKET_READY_END,
  PHONEME_CLOUD_CLICKABLE,
  PHONEME_CLOUD_READY_END,
  PHONEME_FIRST_CLICK,
  PHONEME_INSTRUCTION,
} from "./actionIdentifiers";
import { State } from "../store";
import { get, srcPath } from "../../util/Path";
import { findJsonValue, audioErr } from "../../util/Util";
import {
  BALLOON_STAGE,
  BASKET_STAGE,
  CLOUD_STAGE,
} from "../reducers/stateIdentifiers";
import { gameConfigLoad } from "./shared";

export const phonemeAudio = {
  correct: new Audio(srcPath("shared/clapping.mp3")),
  wrong: new Audio(srcPath("phoneme/wrong.wav")),
  cloud: new Audio(),
  finish: new Audio(),
};

export const importSpec = (grade: number, unit: number, game: number) => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const res = await get("config/phoneme.json");
    const phonemeList = res.data;

    const mainPhonics = findJsonValue(phonemeList, "main_phonics", {
      grade,
      unit,
      game,
    });

    const phonics = findJsonValue(
      phonemeList,
      "phonics",
      { grade, unit, game },
      true
    );

    const balloonPhonics = findJsonValue(
      phonemeList,
      "balloon_phonics",
      { grade, unit, game },
      true
    );

    let wrongPhonics = findJsonValue(
      phonemeList,
      "wrong_phonics",
      { grade, unit, game },
      true
    );

    const words = findJsonValue(
      phonemeList,
      "words",
      { grade, unit, game },
      true
    );

    const wrongWords = findJsonValue(
      phonemeList,
      "wrong_words",
      { grade, unit, game },
      true
    );

    const balloonQty = findJsonValue(phonemeList, "balloon_phonics_qty", {
      grade,
      unit,
      game,
    });

    const basketQty = findJsonValue(phonemeList, "basket_word_qty", {
      grade,
      unit,
      game,
    });

    const cloudTotalRound = findJsonValue(phonemeList, "cloud_rounds", {
      grade,
      unit,
      game,
    });

    if (!wrongPhonics || wrongPhonics.length === 0) {
      const phonicsRes = await get("config/phonics-list.json");
      wrongPhonics = phonicsRes.data;
    }

    dispatch({
      type: PHONEME_IMPORT_SPEC,
      payload: {
        mainPhonics,
        phonics,
        balloonPhonics,
        wrongPhonics,
        words,
        wrongWords,
        balloonQty,
        basketQty,
        cloudTotalRound,
      },
    });

    dispatch(gameConfigLoad());
  };
};

export const backToIntro = () => {
  return { type: PHONEME_BACK_TO_INTRO };
};

export const firstClick = () => {
  return { type: PHONEME_FIRST_CLICK };
};

export const openInstruction = () => {
  return { type: PHONEME_INSTRUCTION, payload: { instruction: true } };
};

export const closeInstruction = () => {
  return { type: PHONEME_INSTRUCTION, payload: { instruction: false } };
};

export const startBalloon = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { balloonQty, balloonPhonics, wrongPhonics } = getState().phoneme;

    let balloons = wrongPhonics.filter((p) => !balloonPhonics.includes(p));
    balloons.sort(() => Math.random() - 0.5);
    balloons = balloons.slice(0, balloonQty - balloonPhonics.length);
    balloons = balloons.concat(balloonPhonics);
    balloons.sort(() => Math.random() - 0.5);

    const balloonColors: string[] = [];
    balloons.forEach((_) => {
      const rand = Math.floor(Math.random() * 4);
      let color: string;
      switch (rand) {
        case 0:
          color = "blue";
          break;
        case 1:
          color = "orange";
          break;
        case 2:
          color = "red";
          break;
        default:
          color = "yellow";
      }
      balloonColors.push(color);
    });

    dispatch({
      type: PHONEME_BALLOON_START,
      payload: {
        stage: BALLOON_STAGE,
        balloons,
        balloonAnswered: [],
        balloonColors,
        balloonReady: false,
        balloonCanSelect: false,
        balloonReadyExit: false,
      },
    });
  };
};

export const readyBalloon = () => {
  return { type: PHONEME_BALLOON_READY };
};

export const selectBalloon = (index: number) => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const { stage, balloons } = getState().phoneme;
    if (stage !== BALLOON_STAGE) return;

    const phonics = balloons[index];
    const audioSrc = `phoneme/phonics_audios/Pronunciation_Card_${phonics}.wav`;
    const audio = new Audio(srcPath(audioSrc));

    const balloonAnswered = [...getState().phoneme.balloonAnswered];
    balloonAnswered.push(index);
    dispatch({
      type: PHONEME_BALLOON_SELECT,
      payload: { balloonAnswered, balloonAnswering: index },
    });

    audio.play().catch(() => {
      audioErr(audioSrc);
      dispatch({ type: PHONEME_BALLOON_CLEAR_ANSWER });
    });
    audio.onended = () => dispatch({ type: PHONEME_BALLOON_CLEAR_ANSWER });
  };
};

export const checkBalloonEnd = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { balloonAnswered, balloons, balloonPhonics } = getState().phoneme;
    const answered = balloonAnswered.map((i) => balloons[i]);

    const isEnd = balloonPhonics.every((b) => {
      const index = answered.indexOf(b);
      if (index >= 0) {
        answered.splice(index, 1);
        return true;
      }
      return false;
    });

    if (isEnd) {
      dispatch({ type: PHONEME_BALLON_NOT_CLICKABLE });
      setTimeout(() => {
        if (!phonemeAudio.finish.src) {
          phonemeAudio.finish.src = srcPath("phoneme/finish.wav");
        }
        phonemeAudio.finish.play();
        phonemeAudio.finish.onended = () => {
          dispatch({ type: PHONEME_BALLON_READY_END });
          setTimeout(() => dispatch({ type: PHONEME_BALLON_END }), 1200);
        };
      }, 2000);
    }
  };
};

export const startBasket = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { basketQty, words, wrongWords } = getState().phoneme;

    let basketItems = wrongWords.filter((w) => !words.includes(w));
    basketItems.sort(() => Math.random() - 0.5);
    basketItems = basketItems.slice(0, basketQty - words.length);
    basketItems = basketItems.concat(words);
    basketItems.sort(() => Math.random() - 0.5);

    dispatch({
      type: PHONEME_BASKET_START,
      payload: {
        stage: BASKET_STAGE,
        basketItems,
        inBasket: [],
        basketChecking: false,
        basketResponse: null,
        basketReadyEnd: false,
      },
    });
  };
};

export const selectBasketItem = (word: string) => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const { inBasket, mainPhonics } = getState().phoneme;

    const audioPath = `${mainPhonics}/Worksheet_Vocab_${mainPhonics}_${word}.wav`;
    const audioSrc = `phoneme/word_audios/${audioPath}`;
    const wordAudio = new Audio(srcPath(audioSrc));

    wordAudio.play().catch(() => {
      audioErr(audioSrc);
      dispatch(responseBasketItem(word));
    });
    wordAudio.onended = () => {
      dispatch(responseBasketItem(word));
    };

    const newInBasket = [...inBasket, word];
    dispatch({
      type: PHONEME_BASKET_SELECT,
      payload: {
        inBasket: newInBasket,
        basketChecking: true,
      },
    });
  };
};

const responseBasketItem = (word: string) => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { stage, words, inBasket } = getState().phoneme;
    if (stage !== BASKET_STAGE) return;

    const response = words.includes(word) ? "correct" : "wrong";
    phonemeAudio[response].play();
    phonemeAudio[response].onended = () => dispatch(clearInBasket());

    const index = inBasket.length - 1;

    dispatch({
      type: PHONEME_BASKET_RESPONSE,
      payload: { basketResponse: { response, index } },
    });
  };
};

const clearInBasket = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { stage, inBasket, words } = getState().phoneme;
    if (stage !== BASKET_STAGE) return;

    const newInBasket = inBasket.filter((b) => words.includes(b));
    const isEnd = words.every((w) => inBasket.includes(w));

    dispatch({
      type: PHONEME_BASKET_CLEAR,
      payload: {
        inBasket: newInBasket,
        basketChecking: isEnd,
        basketResponse: null,
      },
    });

    if (isEnd) {
      setTimeout(() => dispatch(endBasket()), 500);
    }
  };
};

const endBasket = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { stage } = getState().phoneme;
    if (stage !== BASKET_STAGE) return;

    if (!phonemeAudio.finish.src) {
      phonemeAudio.finish.src = srcPath("phoneme/finish.wav");
    }
    phonemeAudio.finish.play();
    phonemeAudio.finish.onended = () => {
      dispatch({ type: PHONEME_BASKET_READY_END });
      setTimeout(() => dispatch({ type: PHONEME_BASKET_END }), 500);
    };
  };
};

export const startCloud = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    dispatch({
      type: PHONEME_CLOUD_START,
      payload: {
        stage: CLOUD_STAGE,
        cloudRound: 0,
        cloudAnswer: null,
        cloudDisplay: [],
        cloudCovered: [],
        cloudPlaying: false,
        cloudPlayIndex: 0,
        cloudPlayNext: false,
        cloudReadyEnd: false,
      },
    });
  };
};

export const cloudSetRound = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { stage, cloudRound, phonics, wrongPhonics } = getState().phoneme;
    if (stage !== CLOUD_STAGE) return;

    const newRound = cloudRound + 1;

    let cloudDisplay = wrongPhonics.filter((p) => !phonics.includes(p));
    cloudDisplay.sort(() => Math.random() - 0.5);
    cloudDisplay = cloudDisplay.slice(0, 2);

    const index = Math.floor(Math.random() * phonics.length);
    const cloudAnswer = phonics[index];

    cloudDisplay = cloudDisplay.concat(cloudAnswer);
    cloudDisplay.sort(() => Math.random() - 0.5);

    dispatch({
      type: PHONEME_CLOUD_SET_ROUND,
      payload: {
        cloudRound: newRound,
        cloudDisplay,
        cloudAnswer,
        cloudCovered: cloudDisplay,
        cloudPlaying: false,
        cloudPlayIndex: 0,
        cloudPlayNext: false,
        cloudClickable: false,
      },
    });

    setTimeout(() => {
      dispatch({ type: PHONEME_CLOUD_PLAY_NEXT });
      dispatch({ type: PHONEME_CLOUD_CLICKABLE });
    }, 1000);
  };
};

const getNextCloudIndex = (currentIndex: number, arr: string[]): number => {
  if (arr.every((v) => v === null)) return -1;
  let newIndex = currentIndex + 1;
  if (newIndex >= arr.length) newIndex = 0;
  if (!arr[newIndex]) newIndex = getNextCloudIndex(newIndex, arr);
  return newIndex;
};

export const cloudPlay = () => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const { stage, cloudPlayIndex, cloudCovered } = getState().phoneme;
    if (stage !== CLOUD_STAGE) return;
    const newIndex = getNextCloudIndex(cloudPlayIndex, cloudCovered);

    if (newIndex !== -1) {
      const audioPath = `Pronunciation_Card_${cloudCovered[newIndex]}.wav`;
      const audioSrc = `phoneme/phonics_audios/${audioPath}`;
      phonemeAudio.cloud.src = srcPath(audioSrc);

      phonemeAudio.cloud.play().catch(() => {
        audioErr(audioSrc);
        setTimeout(() => {
          dispatch({ type: PHONEME_CLOUD_PLAY_END });
          setTimeout(() => dispatch({ type: PHONEME_CLOUD_PLAY_NEXT }), 1200);
        }, 500);
      });
      phonemeAudio.cloud.onended = () => {
        dispatch({ type: PHONEME_CLOUD_PLAY_END });
        setTimeout(() => dispatch({ type: PHONEME_CLOUD_PLAY_NEXT }), 1200);
      };

      dispatch({
        type: PHONEME_CLOUD_PLAY,
        payload: {
          cloudPlaying: true,
          cloudPlayIndex: newIndex,
          cloudPlayNext: false,
        },
      });
    }
  };
};

export const clickCloud = (index: number) => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const { stage, cloudCovered, cloudAnswer } = getState().phoneme;
    if (stage !== CLOUD_STAGE) return;

    const newCovered = [...cloudCovered];
    const selected = newCovered[index];

    const response = selected === cloudAnswer ? "correct" : "wrong";
    phonemeAudio[response].play();
    if (response === "wrong") {
      phonemeAudio[response].onended = () => {
        dispatch({ type: PHONEME_CLOUD_CLICKABLE });
      };
    }

    newCovered[index] = null;

    dispatch({
      type: PHONEME_CLOUD_CLICK,
      payload: { cloudCovered: newCovered, cloudClickable: false },
    });
  };
};

export const endCloud = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { stage } = getState().phoneme;
    if (stage !== CLOUD_STAGE) return;

    if (!phonemeAudio.finish.src) {
      phonemeAudio.finish.src = srcPath("phoneme/finish.wav");
    }
    phonemeAudio.finish.play();

    phonemeAudio.finish.onended = () => {
      dispatch({ type: PHONEME_CLOUD_READY_END });
      setTimeout(() => dispatch({ type: PHONEME_CLOUD_END }), 1000);
    };
  };
};
